import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { useState } from 'react';
import {
  Edit,
  ReferenceField,
  SaveButton,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  useGetOne,
  useGetRecordId,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useTranslate,
} from 'react-admin';
import CloudFrontImageField from 'src/components/CloudFrontImageField';
import { dataProvider } from 'src/providers/data-provider';

export enum SettingKeys {
  form_extract_prompt = 'form_extract_prompt',
  form_extract_prompt_2_1 = 'form_extract_prompt_2_1',
}

const validateJSON = (value: any) => {
  if (!value) return undefined;
  // If value is already an object (from parse), it's valid
  if (typeof value === 'object' && value !== null) return undefined;
  // If it's a string, try to parse it
  if (typeof value === 'string') {
    try {
      JSON.parse(value);
      return undefined;
    } catch (error) {
      return 'Invalid JSON format';
    }
  }
  return 'Invalid JSON format';
};

const DocumentEditContent: React.FC = () => {
  const translate = useTranslate();
  const record = useRecordContext();

  // Fetch form_template data based on form_template_id
  const { data: formTemplate, isLoading: isLoadingTemplate } = useGetOne(
    'form_template',
    { id: record?.form_template_id },
    { enabled: !!record?.form_template_id },
  );

  console.log({ formTemplate });

  return (
    <Grid container spacing={3}>
      {/* Image Upload */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {translate('form_submission.edit.image_upload')}
            </Typography>
            <Box
              sx={{
                width: '400px',
                height: '192px',
                bgcolor: 'grey.100',
                borderRadius: 1,
                border: 1,
                borderColor: 'grey.300',
                mb: 4,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {formTemplate?.image_key ? (
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CloudFrontImageField record={formTemplate} source="image_key" />
                </Box>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CameraAltIcon
                    sx={{
                      color: 'grey.400',
                      fontSize: 48,
                      mb: 1,
                    }}
                  />
                  <Typography>{translate('form_submission.edit.no_image')}</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Form Fields */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {translate('form_submission.edit.form_info')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {isLoadingTemplate ? (
                  <Typography>Loading template...</Typography>
                ) : formTemplate ? (
                  <Box>
                    <Typography variant="h6" component="div" gutterBottom>
                      {formTemplate.name}
                    </Typography>
                    {formTemplate.description && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {formTemplate.description}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <ReferenceField source="form_template_id" reference="form_template">
                    <TextField source="name" variant="h6" component="div" />
                  </ReferenceField>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  source="expected_data"
                  multiline
                  rows={4}
                  fullWidth
                  label={translate('form_submission.fields.expected_data')}
                  helperText="JSON value"
                  validate={validateJSON}
                  format={(value) => {
                    if (!value) return '';
                    if (typeof value === 'string') return value;
                    return JSON.stringify(value, null, 2);
                  }}
                  parse={(value) => {
                    if (!value || value.trim() === '') return null;
                    try {
                      return JSON.parse(value);
                    } catch {
                      return value;
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextInput
                  source="note"
                  multiline
                  rows={2}
                  fullWidth
                  label={translate('form_submission.fields.note')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export const DocumentEdit = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const record = useRecordContext();
  const recordId = useGetRecordId();
  const refresh = useRefresh();

  const handleSubmit = async (formData: any) => {
    try {
      setIsSubmitting(true);

      console.log({ formData, record, recordId });

      const gqlQuery = `
        mutation request_admin_update_form_submission($params: request_admin_update_form_submission_input!) {
          request_admin_update_form_submission(params: $params) {
            error
          }
        }
      `;

      const variables = {
        params: {
          id: parseInt(recordId || record?.id || formData.id),
          expected_data: formData.expected_data,
          note: formData.note || null,
        },
      };

      const response = await dataProvider.create('form_submission', {
        data: formData,
        meta: {
          gqlQuery,
          variables,
        },
      });

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      // Refresh cache to get updated data
      refresh();

      notify(translate('form_submission.notification.updated_success'), { type: 'success' });
      redirect('list', 'form_submission');
    } catch (error) {
      console.error('Update error:', error);
      notify(translate('form_submission.notification.updated_error'), { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Edit title={translate('form_submission.edit.title')}>
      <SimpleForm
        onSubmit={handleSubmit}
        toolbar={
          <Toolbar>
            <SaveButton disabled={isSubmitting} />
          </Toolbar>
        }
      >
        <DocumentEditContent />
      </SimpleForm>
    </Edit>
  );
};

export default DocumentEdit;
